/**
 * Bar staff recording how many people showed up.
 *
 * The booking follows the attendance upwards (option B): more people than
 * booked raises its participant count, which is re-priced, so the booking,
 * reports and invoice match. Bar staff can never lower it: fewer people only
 * records attendance (no-shows still pay), and to lower the booking they ask
 * booking staff or an admin.
 *
 * There is no column for what bar staff added: each raise logs a participant
 * change activity (source BAR_STAFF_ATTENDANCE_SOURCE), and what they added
 * is the sum of those in the booking's history. Booking staff's own changes
 * don't count towards it.
 *
 * The route decides from the booking as stored, so a screen holding a stale
 * booked count can't lower it.
 */

/** Marks a participant change activity logged by bar staff attendance. */
export const BAR_STAFF_ATTENDANCE_SOURCE = "bar_staff_attendance";

/** The parts of a participant change activity this module reads. */
export type ParticipantChangeDetail = {
  source?: string;
  old_count?: number;
  new_count?: number;
};

/**
 * How many participants bar staff added: the sum of every bar staff raise
 * in the booking's history.
 */
export function barStaffAddedParticipants(
  participantChanges: ParticipantChangeDetail[],
): number {
  const added = participantChanges
    .filter((change) => change.source === BAR_STAFF_ATTENDANCE_SOURCE)
    .reduce(
      (sum, change) =>
        sum + (Number(change.new_count) || 0) - (Number(change.old_count) || 0),
      0,
    );
  return Math.max(0, added);
}

/**
 * The booked count after recording attendance: raised to the attendance when
 * more people showed up, else null (it stays; bar staff can't lower it).
 */
export function attendanceBookedCount(
  showedUp: number,
  bookedCount: number,
): number | null {
  return showedUp > bookedCount ? showedUp : null;
}

/** What bar staff are told when they record fewer people than booked. */
export function barStaffDecreaseNotice(bookedCount: number): string {
  return `Attendance saved. The booking stays at ${bookedCount} participants: bar staff can only add participants. To lower it, contact booking staff or an admin.`;
}

/**
 * The activity's description in each venue language. It is the
 * {{latest_activity}} line of the booking update email sent to the customer,
 * and what booking staff read in the booking's history.
 */
export function barStaffParticipantChangeDescriptions(
  from: number,
  to: number,
): Record<"description" | `description_${"en" | "sv" | "da" | "no"}`, string> {
  const added = to - from;
  const en = `${added} extra participant${added === 1 ? "" : "s"} added by our staff on site (${from} → ${to} participants)`;
  return {
    description: en,
    description_en: en,
    description_sv: `${added} extra deltagare tillagda av vår personal på plats (${from} → ${to} deltagare)`,
    description_da: `${added} ekstra deltagere tilføjet af vores personale på stedet (${from} → ${to} deltagere)`,
    description_no: `${added} ekstra deltakere lagt til av våre ansatte på stedet (${from} → ${to} deltakere)`,
  };
}

/** Request body for the staff booking PUT when bar staff record attendance. */
export function attendanceUpdateBody(
  showedUp: number,
  previousAttendance: number | null | undefined,
) {
  return {
    showed_up_participants: showedUp,
    old_participant_count: previousAttendance ?? 0,
    reason: "Updated attendance count",
  };
}

/**
 * The headcount an invoice bills. `invoiced_participant_count` keeps what the
 * customer was charged for after a late decrease (no refund), but must not
 * hide people added since - e.g. bar staff recording 11 on a booking billed
 * for 10.
 */
export function billedParticipantCount(
  invoicedParticipantCount: number | null | undefined,
  participantCount: number | null | undefined,
): number {
  return Math.max(invoicedParticipantCount || 0, participantCount || 0) || 1;
}
